import i18next from 'i18next'
import * as React from 'react'
import {
    extractFileExtension,
    type FileExtension,
    isPhotoExtension,
    photoFileExtensions,
    videoFileExtensions,
} from '../../../../shared/domain/files/fileExtensions.no-deps'
import { type FileId, generateFileId } from '../../../../shared/domain/files/types.no-deps'
import type { CreateAnswer } from '../../../../shared/domain/flows/answer.no-deps'
import type { LoopPathIds } from '../../../../shared/domain/flows/LoopPath.no-deps'
import {
    type CameraType,
    type ElementId,
    type ExtensibleFile,
    type ExtensibleFilesAnswer,
    getExtensibleFileId,
    type PhotoAnswer,
    type PhotoFile,
    photoFileNameToFileId,
    removeExtensibleFile,
    type VideoFile,
} from '../../../../shared/domain/flows/types.no-deps'
import type { ClientLogger } from '../../../../shared/lib/ClientLogger.no-deps'
import type { PromiseThunkQueue } from '../../../../shared/lib/PromiseThunkQueue.no-deps'
import type { CommonDeps } from '../../base/CommonDeps'
import { renderCatch } from '../../error/renderCatch'
import { type FileUrlStates, withFileUrl } from '../../file/FileUrlManager'
import { ExecutionElement } from '../../flow/ExecutionElement'
import { PhotoPreview } from '../../flow/PhotoPreview'
import { VideoPreview } from '../../flow/VideoPreview'
import type { DeviceIndexLookup } from '../../photo/DeviceIndexLookup'
import { SVG_Edit } from '../../svg/SVG_Edit'
import { SVG_ImageGallery } from '../../svg/SVG_ImageGallery'
import { SVG_Play } from '../../svg/SVG_Play'
import type { FileUploadData } from '../../upload/FileUploadData'
import { initialPromiseState, type PromiseState } from '../../util/PromiseState'
import { CameraOverlay } from '../CameraOverlay'
import { ElementLabel } from '../ElementLabel'
import type { TimelineDetails } from '../TimelineDetails'
import CaptureButtons from './CaptureButtons'
import Loading from './Loading'
import { isMobile } from '../../../../shared/lib/isMobile'

interface Props {
    deps: CommonDeps
    elementId: ElementId
    loopPathIds: LoopPathIds
    label: string
    required: boolean
    answer: PhotoAnswer | ExtensibleFilesAnswer
    validationMessages: string[]
    createAnswer: CreateAnswer<ExtensibleFilesAnswer>
    savePathForRecovery: () => Promise<void>
    selectFile: (
        fileExtensions: FileExtension[],
        isIOS?: boolean,
        type?: 'image' | 'video'
    ) => Promise<FileUploadData[]>
    saveBlob: (blob: Blob) => Promise<FileUploadData>
    cancelUpload: (fileId: FileId, logger: ClientLogger) => Promise<void>
    browserCameraDeviceIndexLookup: DeviceIndexLookup
    kind: CameraType
    fileUrlStates: FileUrlStates
    promiseThunkQueue: PromiseThunkQueue
    cordovaGetMedia?: (uploadSource: 'camera' | 'gallery', mediaType: CameraType) => Promise<string>
    onShowPreviewChange?: (previewOpen: boolean) => void
    allowGallerySelection?: boolean
    recovery?: { elementId: ElementId; value: unknown; clear: () => void }
    mediaType?: CameraType
    saveFile?: (fileId: FileId, path: string) => Promise<void>
    timelineDetails?: TimelineDetails
    forceUseBrowserCameraAPI?: Promise<boolean>
    isIOS?: boolean
    isAndroid?: boolean
    openOptions?: () => void
}

interface State {
    showPreview: FileId
    showBrowserCamera: boolean
    takingPhotoState: PromiseState<void>
    cameraMode: CameraType
}

interface AnswerFileData {
    fileId: FileId
    extension: FileExtension
}

/**
 * Note to all consumers. On Android, when using this component the OS may kill the main process and we need to be able
 * to recover state using the url alone.
 */

export class ElementCamera extends React.Component<Props, State> {
    public state: State = {
        showPreview: null,
        showBrowserCamera: false,
        takingPhotoState: initialPromiseState,
        cameraMode: 'photo',
    }

    public async componentDidMount() {
        const { recovery } = this.props

        if (recovery?.elementId === this.props.elementId && typeof recovery.value === 'string') {
            const value = recovery.value
            await this.props.promiseThunkQueue.push(async () => {
                await this.saveFileAndCreateAnswer(value)
                recovery.clear()
            })
        }
    }

    public render = () => renderCatch(this.props.deps, this.tryRender)

    private get isBrowser() {
        return this.props.cordovaGetMedia == null
    }

    private createAnswer = (files: ExtensibleFile[]) =>
        this.props.createAnswer((base) => ({
            ...base,
            type: 'extensible-files',
            value: files,
        }))

    // Note: the 'capture' part of this logic has been extracted into a separate component; `CaptureMedia`.
    //       If you're making substantial changes here, consider refactoring to use that component instead
    private captureMedia = (uploadSource: 'camera' | 'gallery', mediaType: CameraType) => async () => {
        this.setState({ cameraMode: mediaType })

        await this.props.promiseThunkQueue.push(async () => {
            try {
                const isCordova = this.props.cordovaGetMedia != null && this.props.saveFile != null
                const shouldUseBrowserCameraApiForPhoto =
                    (await this.props.forceUseBrowserCameraAPI) && mediaType === 'photo'
                // If on cordova, use it's plugin to get a photo
                if (isCordova && !shouldUseBrowserCameraApiForPhoto) {
                    await this.props.savePathForRecovery()
                    const blob = await this.props.cordovaGetMedia(uploadSource, mediaType)
                    await this.saveFileAndCreateAnswer(blob)
                    return
                }

                const allowedExtensions = [
                    ...(mediaType === 'photo' || mediaType === 'any' ? photoFileExtensions : []),
                    ...(mediaType === 'video' || mediaType === 'any' ? videoFileExtensions : []),
                ]

                // is PWA and iOS
                const isIOS = isMobile.iOS() ? true : false
                if (isIOS && (mediaType === 'photo' || mediaType === 'video')) {
                    const files = await this.props.selectFile(
                        allowedExtensions,
                        true,
                        mediaType === 'photo' ? 'image' : 'video'
                    )
                    await this.createAnswerWithNewMedia(files)
                    return
                }

                // If on web and taking a new photo, show the browser camera
                if (uploadSource === 'camera') {
                    this.setState({ showBrowserCamera: true })
                    return
                }

                // Otherwise select files to upload

                const files = await this.props.selectFile(allowedExtensions)
                await this.createAnswerWithNewMedia(files)
            } catch (error) {
                this.props.deps.reportError(error)
            }
        })
    }

    private saveFileAndCreateAnswer = async (filePath: string) => {
        const fileId = generateFileId()
        const extension = extractFileExtension(filePath)
        await this.props.saveFile(fileId, filePath)
        await this.createAnswerWithNewMedia([{ fileId, extension }])
    }

    private saveBlobAndCreateAnswer = (blob: Blob) =>
        this.props.saveBlob(blob).then((meta) => this.createAnswerWithNewMedia([meta]))

    private createAnswerWithNewMedia = async (files: AnswerFileData[]) => {
        const fileAnswers = files.map(({ fileId, extension }) =>
            isPhotoExtension(extension)
                ? { type: 'PhotoFile' as const, photo: fileId }
                : { type: 'VideoFile' as const, video: fileId }
        )
        await this.createAnswerWithFiles(fileAnswers)
    }

    private createAnswerWithFiles = async (files: ExtensibleFile[]) => {
        const value = this.getValue().concat(files)
        await this.createAnswer(value)
    }

    private saveAnnotationAndCreateAnswer = async (photoFileId: FileId, blob: Blob) => {
        await this.props.promiseThunkQueue.push(async () => {
            const fileMeta = await this.props.saveBlob(blob)
            const value = this.getValue().map((file) => {
                if (file.type === 'VideoFile') return file
                if (file.photo !== photoFileId) return file
                return { ...file, annotation: fileMeta.fileId }
            })
            await this.createAnswer(value)
        })
    }

    private deleteFile = async (file: ExtensibleFile) => {
        await this.props.promiseThunkQueue.push(async () => {
            const logger = this.props.deps.createLogger('ExecutionPage.deleteFile')

            const fileId = getExtensibleFileId(file)
            const value = removeExtensibleFile(this.getValue(), fileId)
            await this.createAnswer(value)
            await this.props.cancelUpload(fileId, logger)
        })
    }

    private exitPreview = () => {
        this.props.onShowPreviewChange(false)
        this.setState({ showPreview: null })
    }

    private getValue(): ExtensibleFile[] {
        const { answer } = this.props
        if (answer == null) return []

        // If the answer was an old answer from before migration
        if (answer.type === 'photo') {
            // Just map to the new extensible file type
            return answer.value.map((fileName) => ({ type: 'PhotoFile', photo: photoFileNameToFileId(fileName) }))
        }

        return answer.value
    }

    private handleMediaClick = (file: ExtensibleFile) => {
        this.props.onShowPreviewChange(true)
        this.setState({ showPreview: getExtensibleFileId(file) })
    }

    private tryRender = () => {
        const props = this.props
        const files = this.getValue()

        return (
            <ExecutionElement
                deps={props.deps}
                elementId={props.elementId}
                loopPathIds={props.loopPathIds}
                validationMessages={props.validationMessages}
                timelineDetails={props.timelineDetails}
            >
                <ElementLabel
                    required={props.required}
                    label={props.label}
                    validationMessages={props.validationMessages}
                    openOptions={this.props.openOptions}
                />
                <div className="AppCore__flow__ElementCamera">
                    <div className="AppCore__flow__ElementCamera__buttons">
                        <CaptureButtons
                            onClick={this.captureMedia}
                            isBrowser={this.isBrowser}
                            mediaType={props.mediaType}
                        />
                        {props.allowGallerySelection && (
                            <div
                                className="AppCore__flow__ElementCamera__button"
                                data-testid="photo-gallery-button"
                                onClick={this.captureMedia('gallery', this.props.mediaType)}
                            >
                                <div className="AppCore__flow__ElementCamera__button_inner">
                                    <SVG_ImageGallery />
                                    <div className="AppCore__flow__ElementCamera__button_text">
                                        {i18next.t('appcore:element.camera.gallery')}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    {files?.length > 0 && (
                        <div className="AppCore__flow__ElementCamera__photos">
                            {files.map((file) => (
                                <div
                                    key={getExtensibleFileId(file)}
                                    className="AppCore__flow__ElementCamera__photo"
                                    onClick={() => this.handleMediaClick(file)}
                                >
                                    {this.renderFileIcon(file)}
                                    {file.type === 'VideoFile' ? this.renderVideo(file) : this.renderPhoto(file)}
                                </div>
                            ))}
                        </div>
                    )}
                    {this.renderBrowserCamera()}
                    {this.renderPreview()}
                </div>
            </ExecutionElement>
        )
    }

    private renderFileIcon(file: ExtensibleFile) {
        if (file.type === 'VideoFile') return <SVG_Play />

        return (
            <div className="AppCore__flow__ElementCamera__photo-preview-button">
                <SVG_Edit />
            </div>
        )
    }

    private withFileUrl(fileId: FileId, render: (url?: string) => React.ReactElement<unknown>) {
        return withFileUrl(this.props.deps, this.props.fileUrlStates, fileId, render, () => <Loading />)
    }

    private renderVideo(file: VideoFile) {
        // We don't have a proper thumbnail but the browser will
        // render one with the video element, there _should_ be
        // no way of playing this element
        return this.withFileUrl(file.video, (url) => <video preload="auto" src={url} />)
    }

    private renderPhoto(file: PhotoFile) {
        return this.withFileUrl(file.photo, (photoUrl) =>
            this.withFileUrl(file.annotation, (annotationUrl) => (
                <zaptic-annotated-image
                    src={photoUrl}
                    annotation={annotationUrl}
                    data-testid="annotated-image--thumbnail"
                ></zaptic-annotated-image>
            ))
        )
    }

    private renderBrowserCamera() {
        if (!this.state.showBrowserCamera) return null

        return (
            <CameraOverlay
                deps={this.props.deps}
                onClose={() => this.setState({ showBrowserCamera: false })}
                savePhoto={async (blob) => {
                    await this.saveBlobAndCreateAnswer(blob)
                    this.setState({ showBrowserCamera: false })
                }}
                browserCameraDeviceIndexLookup={this.props.browserCameraDeviceIndexLookup}
                mode={this.state.cameraMode}
                isAndroid={this.props.isAndroid}
            />
        )
    }

    private renderPreview() {
        const fileId = this.state.showPreview
        if (fileId == null) return null

        const file = this.getValue().find((file) => getExtensibleFileId(file) === fileId)
        if (file == null) return null

        if (file.type === 'VideoFile') {
            return (
                <VideoPreview
                    deps={this.props.deps}
                    videoId={file.video}
                    deleteVideo={async () => {
                        await this.deleteFile(file)
                        this.exitPreview()
                    }}
                    exitPreview={this.exitPreview}
                    fileUrlStates={this.props.fileUrlStates}
                />
            )
        }

        return (
            <PhotoPreview
                deps={this.props.deps}
                file={file}
                deletePhoto={async () => {
                    await this.deleteFile(file)
                    this.exitPreview()
                }}
                exitPreview={this.exitPreview}
                saveAnnotation={(annotationBlob) => this.saveAnnotationAndCreateAnswer(file.photo, annotationBlob)}
                fileUrlStates={this.props.fileUrlStates}
                isIOS={this.props.isIOS}
            />
        )
    }
}
